import { supabase } from './supabase'

/**
 * A person's language (SHL-25, migration 103).
 *
 * THE LIST IS `app.supported_locales()`'s, and the suite reads the migration to
 * make sure the two agree — a language offered here that the database refuses
 * is a control that fails every time it is used (standing rule 4).
 *
 * Labels are each language's name IN THAT LANGUAGE, deliberately: somebody who
 * cannot read the surrounding English still has to be able to find their own.
 */
export type SupportedLocale = 'en-CA' | 'es-CL'

/**
 * FRENCH IS NOT OFFERED UNTIL THERE ARE FRENCH PAGES (David, 25 Sep; migration
 * 113). A person who picked Français got English screens, and every post and
 * FAQ answer would have started being machine-translated into French for a
 * reader who could not use the page around it. Bringing it back is one
 * release: fr-FR here, in `app.supported_locales()`, and a French column in
 * lib/hubCopy.ts — the web suite reads the newest migration defining the
 * function to make sure this list and the database's agree.
 */
export const SUPPORTED_LOCALES: ReadonlyArray<{ key: SupportedLocale; label: string }> = [
  { key: 'en-CA', label: 'English' },
  { key: 'es-CL', label: 'Español' },
]

export function isSupportedLocale(s: string | null | undefined): s is SupportedLocale {
  return SUPPORTED_LOCALES.some((l) => l.key === s)
}

/**
 * What the choice actually changes, said in the language just chosen.
 *
 * It has to be said, because much of the hub is still English: somebody who
 * picks Español and sees an English screen would conclude the control is
 * broken. Since 24 Sep the locale moves the invitation and the sign-in code,
 * which translation of a post or answer they are shown, the language Ask One
 * answers in, and — for Spanish — the shell, the bell, the welcome and the
 * whole Ask One drawer (lib/hubCopy.ts). The screens themselves (feed, Wiki
 * One, Star One, profile) are still English, and the note says so.
 */
export function languageNote(locale: string): string {
  switch (locale) {
    case 'es-CL':
      return 'Se usa para tus correos, el portal, las notificaciones y Ask One. Algunas pantallas de administración siguen en inglés.'
    default:
      return 'Used for your emails, the hub, notifications and Ask One. Some admin screens are in English only.'
  }
}

/** Label for the language picker, bilingual on purpose — see SUPPORTED_LOCALES.
 *  Since 11 Oct the picker is on #/profile, not in the identity menu. */
export const LANGUAGE_HEADING = 'Language · Idioma'

/** `set_my_locale` takes no person argument and never will (083's shape). */
export async function setMyLocale(locale: SupportedLocale): Promise<void> {
  const { error } = await supabase.rpc('set_my_locale', { p_locale: locale })
  if (error) throw new Error(error.message)
}

/**
 * 168 — languages this person reads as written (David, 11 Oct: "I want to read
 * things in Spanish and English as they come in, but French should be
 * translated"). A REPLACE: send every language that should stay untranslated.
 * The choices offered are the supported languages other than your own, so
 * French appears here on the day it is offered again and not before (a box
 * for a language no post can be written in would do nothing — rule 4).
 */
export function readingChoices(locale: string): ReadonlyArray<{ key: SupportedLocale; label: string }> {
  return SUPPORTED_LOCALES.filter((l) => l.key !== locale)
}

export async function setMyReadingLocales(locales: SupportedLocale[]): Promise<string[]> {
  const { data, error } = await supabase.rpc('set_my_reading_locales', { p_locales: locales })
  if (error) throw new Error(error.message)
  return (data as string[] | null) ?? []
}
